import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Container, Card, Button, Spinner, Alert, Row, Col, Badge, Table } from 'react-bootstrap';
import api, { getPublicUploadsOrigin } from '../services/api';
import Footer from '../components/Footer';

const DetalleConvocatoria = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [convocatoria, setConvocatoria] = useState(null);
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasPostulado, setHasPostulado] = useState(false);

  useEffect(() => {
    loadConvocatoria();
  }, [id]);

  const loadConvocatoria = async () => {
    setLoading(true);
    setError(null);
    try {
      const convocatoriaResponse = await api.get(`/api/v1/convocatorias/public/${id}`);
      setConvocatoria(convocatoriaResponse.data);
      
      const ordenesResponse = await api.get(`/api/v1/ordenes/public/convocatoria/${id}`);
      setOrdenes(ordenesResponse.data || []);

      // Si está autenticado, verificar si ya postuló a esta convocatoria
      try {
        if (isAuthenticated) {
          const cotRes = await api.get(`/api/v1/cotizaciones/convocatoria/${id}`);
          const list = Array.isArray(cotRes.data) ? cotRes.data : [];
          const yaPostulo = list.some((c) => Number(c.id_proveedor) === Number(user?.id_usuario));
          setHasPostulado(yaPostulo);
        } else {
          setHasPostulado(false);
        }
      } catch (innerErr) {
        // Si falla esta verificación, no bloquear la vista
        setHasPostulado(false);
      }
    } catch (error) {
      console.error('Error loading convocatoria:', error);
      setError('Error al cargar los detalles de la convocatoria');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoVariant = (estado) => {
    switch(estado) {
      case 'ABIERTA':
        return 'success';
      case 'CERRADA':
        return 'secondary';
      case 'EVALUACION':
        return 'warning';
      case 'FINALIZADA':
        return 'info';
      default:
        return 'secondary';
    }
  };

  // Función para sanitizar el código de convocatoria (igual que en el backend)
  const sanitizeFragment = (value) => {
    if (!value) return '';
    const trimmed = String(value).trim().replace(/\s+/g, '_');
    return trimmed.replace(/[^a-zA-Z0-9_-]/g, '');
  };

  // Función para construir la URL del archivo PDF
  const getPdfUrl = (convocatoria, field) => {
    if (!convocatoria || !convocatoria[field]) return null;
    const codConvocatoria = sanitizeFragment(convocatoria.COD_CONVOCATORIA || convocatoria.nro_convocatoria);
    return `${getPublicUploadsOrigin()}/uploads/convocatorias/${codConvocatoria}/${convocatoria[field]}`;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Cargando detalles...</p>
        </div>
      </div>
    );
  }

  if (error || !convocatoria) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
        <Container className="py-5">
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5">
              <i className="bi bi-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c' }}></i>
              <h3 className="mt-3 mb-2" style={{ color: '#2b3e53' }}>
                {error ? 'Error' : 'Convocatoria no encontrada'}
              </h3>
              <p className="text-muted mb-4">
                {error || 'La convocatoria solicitada no existe o no está disponible.'}
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/')}
                style={{ backgroundColor: '#2b3e53', borderColor: '#2b3e53' }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Volver al Inicio
              </Button>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      {/* Header */}
      <div style={{ 
        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)), url(/fondo.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: '2rem 0',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        position: 'relative'
      }}>
        <Container>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <img 
                src="/LOGO-DIRESA.png" 
                alt="Logo DIRESA" 
                className="me-3"
                style={{ 
                  height: '90px', 
                  objectFit: 'contain',
                  filter: 'brightness(0) invert(1)'
                }}
              />
              <div>
                <h2 className="text-white mb-1 fw-bold">Detalles de Convocatoria</h2>
                <p className="text-white-50 mb-0 small">DIRESA - Portal Público</p>
              </div>
            </div>
            <Button
              variant="outline-light"
              onClick={() => navigate('/')}
              className="px-4"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Volver al Inicio
            </Button>
          </div>
        </Container>
      </div>

      {/* Contenido */}
      <Container className="py-5">
        {/* Información General */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h3 className="fw-bold mb-2" style={{ color: '#2b3e53' }}>
                  <i className="bi bi-file-earmark-text me-2"></i>
                  {convocatoria.COD_CONVOCATORIA || convocatoria.nro_convocatoria}
                </h3>
                <p className="text-muted mb-0 small">Código de Convocatoria</p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Badge bg={getEstadoVariant(convocatoria.estado)} className="px-3 py-2 fs-6">
                  {convocatoria.estado}
                </Badge>
                {convocatoria.estado === 'ABIERTA' && !convocatoria.orden_notificada && (
                  hasPostulado ? (
                    <Button
                      variant="secondary"
                      disabled
                      className="px-4"
                      title="Ya postulaste a esta convocatoria"
                    >
                      <i className="bi bi-check2-circle me-2"></i>
                      Ya postulaste
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      onClick={() => {
                        if (!isAuthenticated) {
                          // Guardar la ruta de destino para volver después del login
                          localStorage.setItem('redirectAfterLogin', `/postular-cotizacion/${convocatoria.nro_convocatoria}`);
                          navigate('/login');
                        } else {
                          navigate(`/postular-cotizacion/${convocatoria.nro_convocatoria}`);
                        }
                      }}
                      className="px-4"
                    >
                      <i className="bi bi-send me-2"></i>
                      Postular
                    </Button>
                  )
                )}
              </div>
            </div>

            {/* Detalles de la Convocatoria y Documentos - Compartiendo espacio */}
            <Row className="g-4 mt-0">
              {/* Columna izquierda: Detalles reducidos */}
              <Col md={12} lg={5}>
                <div className="p-4 border rounded h-100" style={{ 
                  backgroundColor: '#f8f9fa',
                  borderColor: '#dee2e6',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  <div className="d-flex align-items-center mb-3">
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(43, 62, 83, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px'
                    }}>
                      <i className="bi bi-info-circle" style={{ color: '#2b3e53', fontSize: '1.1rem' }}></i>
                    </div>
                    <h6 className="mb-0 fw-bold" style={{ color: '#2b3e53' }}>Detalles de la Convocatoria</h6>
                  </div>
                  
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom" style={{ borderColor: '#e9ecef !important' }}>
                      <small className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Código de Convocatoria:</small>
                      <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>
                        {convocatoria.COD_CONVOCATORIA || convocatoria.nro_convocatoria || 'N/A'}
                      </span>
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom" style={{ borderColor: '#e9ecef !important' }}>
                      <small className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Tipo de Bien:</small>
                      <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>
                        {convocatoria.tipo_bien === 'S' ? 'Servicio' : convocatoria.tipo_bien === 'B' ? 'Bienes' : 'N/A'}
                      </span>
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom" style={{ borderColor: '#e9ecef !important' }}>
                      <small className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Año:</small>
                      <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>{convocatoria.ano_eje || 'N/A'}</span>
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom" style={{ borderColor: '#e9ecef !important' }}>
                      <small className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Nro Pedido:</small>
                      <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>{convocatoria.nro_pedido || 'N/A'}</span>
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom" style={{ borderColor: '#e9ecef !important' }}>
                      <small className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Rubro:</small>
                      <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>{convocatoria.rubro || 'N/A'}</span>
                    </div>

              {convocatoria.nombre_ejecutora && (
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom" style={{ borderColor: '#e9ecef !important' }}>
                        <small className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Unidad Ejecutora:</small>
                        <span className="text-dark fw-medium text-end" style={{ fontSize: '0.85rem', maxWidth: '60%' }}>
                          {convocatoria.nombre_ejecutora}
                        </span>
                      </div>
              )}

              {convocatoria.fecha_inicio && (
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom" style={{ borderColor: '#e9ecef !important' }}>
                        <small className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Fecha de Inicio:</small>
                        <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>
                          {new Date(convocatoria.fecha_inicio).toLocaleDateString('es-PE', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
              )}

              {convocatoria.fecha_fin && (
                      <div className="d-flex justify-content-between align-items-center py-2">
                        <small className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Fecha de Fin:</small>
                        <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>
                          {new Date(convocatoria.fecha_fin).toLocaleDateString('es-PE', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    )}
                    </div>
                  </div>
                </Col>

              {/* Columna derecha: Documentos */}
              <Col md={12} lg={7}>
                <div className="p-4 border rounded h-100" style={{ 
                  backgroundColor: '#f8f9fa',
                  borderColor: '#dee2e6',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  <div className="d-flex align-items-center mb-4">
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      backgroundColor: 'rgba(220, 53, 69, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px'
                    }}>
                      <i className="bi bi-file-earmark-pdf" style={{ color: '#dc3545', fontSize: '1.5rem' }}></i>
                    </div>
                    <div>
                      <h5 className="mb-0 fw-bold" style={{ color: '#2b3e53' }}>Documentos de la Convocatoria</h5>
                      <p className="text-muted mb-0 small">Descarga los documentos relacionados</p>
                    </div>
                  </div>
                  <Row className="g-3">
                  {/* Especificaciones Técnicas - Siempre visible */}
                  <Col md={6}>
                    <div 
                      className="p-4 border rounded bg-white h-100"
                      style={{
                        borderColor: '#e9ecef',
                        transition: 'all 0.3s ease',
                        cursor: convocatoria.especificaciones_tecnicas_pdf ? 'pointer' : 'default',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        opacity: convocatoria.especificaciones_tecnicas_pdf ? 1 : 0.7
                      }}
                      onMouseEnter={(e) => {
                        if (convocatoria.especificaciones_tecnicas_pdf) {
                          e.currentTarget.style.borderColor = '#dc3545';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 53, 69, 0.15)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e9ecef';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div className="d-flex align-items-start">
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '12px',
                          backgroundColor: 'rgba(220, 53, 69, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '16px',
                          flexShrink: 0
                        }}>
                          <i className="bi bi-file-pdf" style={{ color: '#dc3545', fontSize: '1.75rem' }}></i>
                        </div>
                        <div style={{ flex: 1 }}>
                          <h6 className="fw-bold mb-2" style={{ color: '#2b3e53' }}>
                            Especificaciones Técnicas
                          </h6>
                          <p className="text-muted small mb-3" style={{ fontSize: '0.875rem' }}>
                            {convocatoria.especificaciones_tecnicas_pdf 
                              ? 'Documento con las especificaciones técnicas detalladas'
                              : 'Documento no disponible aún'}
                          </p>
                          {convocatoria.especificaciones_tecnicas_pdf ? (
                            <a
                              href={getPdfUrl(convocatoria, 'especificaciones_tecnicas_pdf')}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-danger d-inline-flex align-items-center"
                              style={{ borderRadius: '6px' }}
                            >
                              <i className="bi bi-download me-2"></i>
                              Descargar PDF
                            </a>
                          ) : (
                            <button
                              className="btn btn-sm btn-secondary d-inline-flex align-items-center"
                              style={{ borderRadius: '6px' }}
                              disabled
                            >
                              <i className="bi bi-file-x me-2"></i>
                              No disponible
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Col>
                  {convocatoria.solicitud_cotizacion_pdf ? (
                    <Col md={6}>
                      <div 
                        className="p-4 border rounded bg-white h-100"
                        style={{
                          borderColor: '#e9ecef',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#0d6efd';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(13, 110, 253, 0.15)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e9ecef';
                          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <div className="d-flex align-items-start">
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            backgroundColor: 'rgba(13, 110, 253, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '16px',
                            flexShrink: 0
                          }}>
                            <i className="bi bi-file-pdf" style={{ color: '#0d6efd', fontSize: '1.75rem' }}></i>
                          </div>
                          <div style={{ flex: 1 }}>
                            <h6 className="fw-bold mb-2" style={{ color: '#2b3e53' }}>
                              Solicitud de Cotización
                            </h6>
                            <p className="text-muted small mb-3" style={{ fontSize: '0.875rem' }}>
                              Documento generado automáticamente con los datos de la convocatoria
                            </p>
                            <a
                              href={getPdfUrl(convocatoria, 'solicitud_cotizacion_pdf')}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-primary d-inline-flex align-items-center"
                              style={{ borderRadius: '6px' }}
                            >
                              <i className="bi bi-download me-2"></i>
                              Descargar PDF
                            </a>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ) : null}

                  <Col md={6}>
                    <div
                      className="p-4 border rounded bg-white h-100"
                      style={{
                        borderColor: '#e9ecef',
                        transition: 'all 0.3s ease',
                        cursor: convocatoria.formatos_postulacion_pdf ? 'pointer' : 'default',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        opacity: convocatoria.formatos_postulacion_pdf ? 1 : 0.7
                      }}
                      onMouseEnter={(e) => {
                        if (convocatoria.formatos_postulacion_pdf) {
                          e.currentTarget.style.borderColor = '#fd7e14';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(253, 126, 20, 0.15)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e9ecef';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div className="d-flex align-items-start">
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '12px',
                          backgroundColor: 'rgba(253, 126, 20, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '16px',
                          flexShrink: 0
                        }}>
                          <i className="bi bi-file-pdf" style={{ color: '#fd7e14', fontSize: '1.75rem' }}></i>
                        </div>
                        <div style={{ flex: 1 }}>
                          <h6 className="fw-bold mb-2" style={{ color: '#2b3e53' }}>Formatos de postulación</h6>
                          <p className="text-muted small mb-3" style={{ fontSize: '0.875rem' }}>
                            {convocatoria.formatos_postulacion_pdf
                              ? 'Documento con formatos para la postulación'
                              : 'Documento no disponible aún'}
                          </p>
                          {convocatoria.formatos_postulacion_pdf ? (
                            <a
                              href={getPdfUrl(convocatoria, 'formatos_postulacion_pdf')}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm d-inline-flex align-items-center"
                              style={{ borderRadius: '6px', backgroundColor: '#fd7e14', borderColor: '#fd7e14', color: '#fff' }}
                            >
                              <i className="bi bi-download me-2"></i>
                              Descargar PDF
                            </a>
                          ) : (
                            <button type="button" className="btn btn-sm btn-secondary" disabled style={{ borderRadius: '6px' }}>
                              <i className="bi bi-file-x me-2"></i>
                              No disponible
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div
                      className="p-4 border rounded bg-white h-100"
                      style={{
                        borderColor: '#e9ecef',
                        transition: 'all 0.3s ease',
                        cursor: convocatoria.otros_documentos_pdf ? 'pointer' : 'default',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        opacity: convocatoria.otros_documentos_pdf ? 1 : 0.7
                      }}
                      onMouseEnter={(e) => {
                        if (convocatoria.otros_documentos_pdf) {
                          e.currentTarget.style.borderColor = '#6f42c1';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(111, 66, 193, 0.15)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e9ecef';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div className="d-flex align-items-start">
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '12px',
                          backgroundColor: 'rgba(111, 66, 193, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '16px',
                          flexShrink: 0
                        }}>
                          <i className="bi bi-file-pdf" style={{ color: '#6f42c1', fontSize: '1.75rem' }}></i>
                        </div>
                        <div style={{ flex: 1 }}>
                          <h6 className="fw-bold mb-2" style={{ color: '#2b3e53' }}>Otros documentos</h6>
                          <p className="text-muted small mb-3" style={{ fontSize: '0.875rem' }}>
                            {convocatoria.otros_documentos_pdf
                              ? 'Documentación complementaria de la convocatoria'
                              : 'Documento no disponible aún'}
                          </p>
                          {convocatoria.otros_documentos_pdf ? (
                            <a
                              href={getPdfUrl(convocatoria, 'otros_documentos_pdf')}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm d-inline-flex align-items-center"
                              style={{ borderRadius: '6px', backgroundColor: '#6f42c1', borderColor: '#6f42c1', color: '#fff' }}
                            >
                              <i className="bi bi-download me-2"></i>
                              Descargar PDF
                            </a>
                          ) : (
                            <button type="button" className="btn btn-sm btn-secondary" disabled style={{ borderRadius: '6px' }}>
                              <i className="bi bi-file-x me-2"></i>
                              No disponible
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Col>
                  </Row>
                </div>
              </Col>
            </Row>

            {convocatoria.motivo_pedido && (
              <div className="mt-4 p-4 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="d-flex align-items-center mb-3">
                  <i className="bi bi-file-text me-2" style={{ color: '#2b3e53', fontSize: '1.25rem' }}></i>
                  <h5 className="mb-0 fw-bold" style={{ color: '#2b3e53' }}>Motivo del Pedido</h5>
                </div>
                <p className="text-dark mb-0" style={{ lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
                  {convocatoria.motivo_pedido}
                </p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Items de la Convocatoria */}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div>
                <h4 className="fw-bold mb-1" style={{ color: '#2b3e53' }}>
                  <i className="bi bi-list-check me-2"></i>
                  Items Solicitados
                </h4>
                <p className="text-muted mb-0 small">
                  Total de items: <strong>{ordenes.length}</strong>
                </p>
              </div>
            </div>
            
            {ordenes.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#adb5bd' }}></i>
                <p className="mt-3 text-muted mb-0">
                  No hay items registrados para esta convocatoria.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead style={{ backgroundColor: '#f8f9fa', color: '#ffffff' }}>
                    <tr>
                      <th className="border-0 py-3">Secuencia</th>
                      <th className="border-0 py-3">Item</th>
                      <th className="border-0 py-3">Nombre Item</th>
                      <th className="border-0 py-3 text-end">Cantidad</th>
                      <th className="border-0 py-3">Unidad Medida</th>
                      <th className="border-0 py-3">Características</th>
                      <th className="border-0 py-3">Detalles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordenes.map((orden, index) => (
                      <tr key={orden.id_orden} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                        <td className="py-3">{orden.secuencia}</td>
                        <td className="py-3">
                          <code className="text-primary">
                            {orden.grupo_bien}-{orden.clase_bien}-{orden.familia_bien}-{orden.item_bien}
                          </code>
                        </td>
                        <td className="py-3">{orden.nombre_item || 'N/A'}</td>
                        <td className="py-3 text-end fw-semibold">
                          {Math.round(Number(orden.cant_solicitada || 0))}
                        </td>
                        <td className="py-3">{orden.unidad_medida || 'UN - Unidad'}</td>
                        <td className="py-3">{orden.caracteristicas || '-'}</td>
                        <td className="py-3">{orden.detalles || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
      <Footer />
    </div>
  );
};

export default DetalleConvocatoria;
